import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval,
  getDay,
  addMonths,
  subMonths,
  isToday,
  isSameMonth,
  isSameDay,
  parseISO,
  parse,
  isValid
} from 'date-fns';
import { pl } from 'date-fns/locale';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import Modal from '../components/common/Modal';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  CalendarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

interface TeamLeave {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  leaveType: {
    id: number;
    name: string;
    code: string;
  };
  user: {
    id: string;
    firstName: string;
    lastName: string;
    isActive?: boolean;
    department?: {
      id: number;
      name: string;
    };
    departments?: Array<{
      id: number;
      name: string;
    }>;
    teams?: Array<{
      id: number;
      name: string;
      department_id: number;
    }>;
  };
  status: string;
}

export const TeamCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const monthPickerRef = useRef<HTMLInputElement>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [showDayModal, setShowDayModal] = useState(false);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Fetch departments
  const { data: departments, isError: departmentsError } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        console.log('Fetching departments for calendar...');
        const response = await api.get('/departments');
        console.log('Departments response:', response.data);
        return response.data.departments || response.data.data || response.data || [];
      } catch (error) {
        console.error('Error fetching departments:', error);
        return [];
      }
    },
    retry: 2,
    retryDelay: 1000
  });

  // Fetch team leaves for the current month
  const { data: allLeaves, isLoading } = useQuery({
    queryKey: ['teamLeaves', monthStart, monthEnd],
    queryFn: async () => {
      try {
        const params: any = {
          start_date: format(monthStart, 'yyyy-MM-dd'),
          end_date: format(monthEnd, 'yyyy-MM-dd'),
          status: 'approved',
          include_user: true // Request user information to validate
        };
        
        console.log('Fetching team leaves with params:', params);
        const response = await api.get('/leaves', { params });
        console.log('Team leaves response:', response.data);
        
        // Handle different response structures
        const leaves = response.data.leaveRequests || response.data.leaves || response.data.data || response.data || [];
        
        console.log('First leave example:', leaves[0]);
        
        // Filter out invalid leaves and map to expected structure
        return leaves
          .filter((leave: any) => {
            // Filter out leaves with invalid or missing data
            if (!leave.id || !leave.start_date || !leave.end_date) {
              console.warn('Filtering out leave with missing required data:', leave);
              return false;
            }
            
            // Filter out leaves where user doesn't exist or is inactive
            if (!leave.user || !leave.user.id) {
              console.warn('Filtering out leave with missing user:', leave);
              return false;
            }
            
            // Check if user is active (if field is available)
            if (leave.user.is_active === false) {
              console.warn('Filtering out leave for inactive user:', leave.user);
              return false;
            }
            
            // Filter out cancelled or rejected leaves that might have slipped through
            const status = (leave.status || '').toLowerCase();
            if (['cancelled', 'canceled', 'rejected', 'deleted'].includes(status)) {
              console.warn('Filtering out non-approved leave:', leave);
              return false;
            }
            
            return true;
          })
          .map((leave: any) => ({
            id: leave.id,
            userId: leave.user_id || leave.userId,
            startDate: leave.start_date || leave.startDate,
            endDate: leave.end_date || leave.endDate,
            leaveType: leave.leaveType || leave.leave_type,
            user: {
              id: leave.user?.id || leave.user_id || leave.userId,
              firstName: leave.user?.first_name || leave.user?.firstName || 'Unknown',
              lastName: leave.user?.last_name || leave.user?.lastName || 'User',
              department: leave.user?.department || (leave.user?.departments && leave.user.departments[0]) || null,
              departments: leave.user?.departments || [],
              teams: leave.user?.teams || [],
              isActive: leave.user?.is_active !== false // Default to true if not specified
            },
            status: leave.status
          }));
      } catch (error) {
        console.error('Error fetching team leaves:', error);
        return [];
      }
    },
    retry: 2,
    retryDelay: 1000
  });

  // Filter leaves based on selected department
  const teamLeaves = allLeaves?.filter((leave: TeamLeave) => {
    // Additional safety check for active users
    if (leave.user?.isActive === false) {
      return false;
    }
    
    if (selectedDepartment === 'all') {
      return true;
    }
    
    console.log('Filtering leave for user:', leave.user?.firstName, leave.user?.lastName, {
      selectedDepartment,
      directDepartment: leave.user?.department,
      departments: leave.user?.departments,
      teams: leave.user?.teams
    });
    
    // Check user's direct department
    if (leave.user?.department?.id && leave.user.department.id.toString() === selectedDepartment) {
      console.log('Match found through direct department');
      return true;
    }
    
    // Check user's departments array
    if (leave.user?.departments && Array.isArray(leave.user.departments)) {
      const matchesDepartment = leave.user.departments.some((dept: any) => 
        dept.id && dept.id.toString() === selectedDepartment
      );
      if (matchesDepartment) {
        console.log('Match found through departments array');
        return true;
      }
    }
    
    // Check department through teams
    if (leave.user?.teams && Array.isArray(leave.user.teams) && departments) {
      const matchesThroughTeam = leave.user.teams.some((team: any) => {
        const teamDepartment = departments.find((dept: any) => dept.id === team.department_id);
        console.log('Checking team:', team.name, 'department_id:', team.department_id, 'found department:', teamDepartment);
        return teamDepartment && teamDepartment.id.toString() === selectedDepartment;
      });
      if (matchesThroughTeam) {
        console.log('Match found through teams');
        return true;
      }
    }
    
    console.log('No match found for user');
    return false;
  }) || [];

  console.log('Filtered leaves count:', teamLeaves?.length, 'from total:', allLeaves?.length);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(direction === 'prev' 
      ? subMonths(currentMonth, 1) 
      : addMonths(currentMonth, 1)
    );
  };

  const handleMonthPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (inputValue) {
      try {
        // Input format is YYYY-MM
        const selectedDate = parse(inputValue + '-01', 'yyyy-MM-dd', new Date());
        if (isValid(selectedDate)) {
          setCurrentMonth(selectedDate);
          setShowMonthPicker(false);
        }
      } catch (error) {
        console.error('Invalid date selected:', error);
      }
    }
  };

  const openMonthPicker = () => {
    setShowMonthPicker(true);
    // Focus the input after a brief delay to ensure it's rendered
    setTimeout(() => {
      monthPickerRef.current?.focus();
    }, 10);
  };

  const handleDayClick = (day: Date, leaves: TeamLeave[]) => {
    if (leaves.length > 0) {
      setSelectedDay(day);
      setShowDayModal(true);
    }
  };

  const getSelectedDayLeaves = () => {
    if (!selectedDay) return [];
    return getLeaveForDay(selectedDay, teamLeaves);
  };

  const getLeaveForDay = (day: Date, leaves: TeamLeave[] = []) => {
    return leaves.filter(leave => {
      const leaveStart = parseISO(leave.startDate);
      const leaveEnd = parseISO(leave.endDate);
      return day >= leaveStart && day <= leaveEnd;
    });
  };

  const weekDays = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Ndz'];
  const firstDayOfMonth = getDay(monthStart);
  
  // Create empty cells for days before the first day of month
  // getDay() returns 0 for Sunday, 1 for Monday, etc.
  // We want Monday to be day 0, so we adjust: (getDay() + 6) % 7
  const adjustedFirstDay = (firstDayOfMonth + 6) % 7;
  const emptyCellsStart = Array(adjustedFirstDay).fill(null);
  
  // Calculate empty cells needed at the end to complete the grid
  const totalCells = daysInMonth.length + adjustedFirstDay;
  const totalRows = Math.ceil(totalCells / 7);
  const emptyCellsEnd = Array((totalRows * 7) - totalCells).fill(null);

  if (isLoading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Kalendarz zespołu</h1>
        <p className="mt-1 text-sm text-gray-500">
          Zobacz planowane urlopy i dostępność członków zespołu
        </p>
        {departmentsError && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-800 text-sm">
              ⚠️ Nie udało się załadować działów. Filtrowanie może nie działać poprawnie.
            </p>
          </div>
        )}
        {selectedDepartment !== 'all' && (
          <div className="mt-2 text-sm text-gray-600">
            Filtrowanie według działu: <strong>{departments?.find(d => d.id.toString() === selectedDepartment)?.name || selectedDepartment}</strong> 
            {teamLeaves && <span className="ml-2">({teamLeaves.length} aktywnych wniosków urlopowych)</span>}
          </div>
        )}
        {allLeaves && teamLeaves && allLeaves.length !== teamLeaves.length && (
          <div className="mt-1 text-xs text-gray-500">
            Odfiltrowano {allLeaves.length - teamLeaves.length} nieaktywnych/nieważnych wniosków
          </div>
        )}
      </div>

      {/* Filters and Navigation */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <select
                className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="all">Wszystkie działy</option>
                {departments?.map((dept: any) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateMonth('prev')}
                title="Poprzedni miesiąc"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </Button>
              
              <div className="relative">
                {showMonthPicker ? (
                  <input
                    ref={monthPickerRef}
                    type="month"
                    value={format(currentMonth, 'yyyy-MM')}
                    onChange={handleMonthPickerChange}
                    onBlur={() => setShowMonthPicker(false)}
                    className="text-lg font-medium text-gray-900 bg-transparent border border-gray-300 rounded px-2 py-1 min-w-[150px] text-center focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <button
                    onClick={openMonthPicker}
                    className="text-lg font-medium text-gray-900 min-w-[150px] text-center hover:bg-gray-100 px-2 py-1 rounded transition-colors duration-150"
                    title="Kliknij aby wybrać miesiąc"
                  >
                    {format(currentMonth, 'MMMM yyyy', { locale: pl })}
                  </button>
                )}
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateMonth('next')}
                title="Następny miesiąc"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(new Date())}
                className="ml-2"
                title="Przejdź do bieżącego miesiąca"
              >
                Dziś
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {/* Week days header */}
            {weekDays.map(day => (
              <div
                key={day}
                className="bg-gray-50 p-2 text-center text-xs font-medium text-gray-700"
              >
                {day}
              </div>
            ))}
            
            {/* Empty cells for alignment at start */}
            {emptyCellsStart.map((_, index) => (
              <div key={`empty-start-${index}`} className="bg-white p-2 min-h-[100px]" />
            ))}
            
            {/* Calendar days */}
            {daysInMonth.map(day => {
              const dayLeaves = getLeaveForDay(day, teamLeaves);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              
              return (
                <div
                  key={day.toISOString()}
                  className={clsx(
                    'bg-white p-2 min-h-[100px] relative',
                    !isCurrentMonth && 'text-gray-400',
                    isToday(day) && 'bg-yellow-50',
                    dayLeaves.length > 0 && 'cursor-pointer hover:bg-gray-50'
                  )}
                  onClick={() => handleDayClick(day, dayLeaves)}
                  title={dayLeaves.length > 0 ? `Kliknij aby zobaczyć wszystkich na urlopie (${dayLeaves.length})` : ''}
                >
                  <div className={clsx(
                    'text-sm font-medium mb-1',
                    isToday(day) && 'text-primary-600'
                  )}>
                    {format(day, 'd')}
                  </div>
                  
                  <div className="space-y-1">
                    {dayLeaves.slice(0, 3).map(leave => (
                      <div
                        key={leave.id}
                        className="text-xs p-1 rounded truncate"
                        style={{
                          backgroundColor: getLeaveTypeColor(leave.leaveType, 0.2),
                          color: getLeaveTypeColor(leave.leaveType, 1)
                        }}
                        title={`${leave.user?.firstName || leave.user?.first_name || 'Użytkownik'} ${leave.user?.lastName || leave.user?.last_name || 'Bez nazwiska'} - ${leave.leaveType?.name || 'Nieznany typ urlopu'}`}
                      >
                        {(leave.user?.firstName || leave.user?.first_name)?.[0] || 'U'}{(leave.user?.lastName || leave.user?.last_name)?.[0] || 'U'}
                      </div>
                    ))}
                    {dayLeaves.length > 3 && (
                      <button 
                        className="text-xs text-primary-600 text-center w-full hover:text-primary-800 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDayClick(day, dayLeaves);
                        }}
                      >
                        +{dayLeaves.length - 3} więcej
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Empty cells for alignment at end */}
            {emptyCellsEnd.map((_, index) => (
              <div key={`empty-end-${index}`} className="bg-white p-2 min-h-[100px]" />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Legend and Summary */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Legenda typów urlopów</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {['wypoczynkowy', 'chorobowy', 'na_zadanie', 'okolicznosciowy', 'bezplatny', 'szkoleniowy'].map(code => (
                <div key={code} className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: getLeaveTypeColor(code, 1) }}
                  />
                  <span className="text-sm text-gray-700">
                    {getLeaveTypeName(code)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Podsumowanie miesięczne</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Osoby na urlopie</span>
                <Badge variant="primary">
                  {new Set(teamLeaves?.map((l: TeamLeave) => l.userId)).size || 0}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Dni urlopowe łącznie</span>
                <Badge variant="primary">
                  {teamLeaves?.reduce((acc: number, leave: TeamLeave) => {
                    const start = parseISO(leave.startDate);
                    const end = parseISO(leave.endDate);
                    const days = eachDayOfInterval({ start, end }).filter(
                      day => isSameMonth(day, currentMonth)
                    ).length;
                    return acc + days;
                  }, 0) || 0}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Najczęstszy typ urlopu</span>
                <Badge variant="secondary">
                  {getMostCommonLeaveType(teamLeaves)}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Day Details Modal */}
      <Modal
        isOpen={showDayModal}
        onClose={() => {
          setShowDayModal(false);
          setSelectedDay(null);
        }}
        title={selectedDay ? `Urlopy - ${format(selectedDay, 'dd MMMM yyyy', { locale: pl })}` : ''}
      >
        <div className="p-6">
          {getSelectedDayLeaves().length > 0 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Użytkownicy na urlopie w tym dniu ({getSelectedDayLeaves().length}):
              </p>
              <div className="space-y-3">
                {getSelectedDayLeaves().map(leave => (
                  <div key={leave.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-500">
                          <span className="text-sm font-medium leading-none text-white">
                            {(leave.user?.firstName || leave.user?.first_name)?.[0] || 'U'}{(leave.user?.lastName || leave.user?.last_name)?.[0] || 'U'}
                          </span>
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {leave.user?.firstName || leave.user?.first_name || 'Użytkownik'} {leave.user?.lastName || leave.user?.last_name || 'Bez nazwiska'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {leave.user?.department?.name || 'Brak działu'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: getLeaveTypeColor(leave.leaveType, 0.2),
                          color: getLeaveTypeColor(leave.leaveType, 1)
                        }}
                      >
                        {leave.leaveType?.name || 'Urlop'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {format(parseISO(leave.startDate), 'dd.MM')} - {format(parseISO(leave.endDate), 'dd.MM')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Brak urlopów w tym dniu
            </p>
          )}
          <div className="mt-6 flex justify-end">
            <Button 
              onClick={() => {
                setShowDayModal(false);
                setSelectedDay(null);
              }}
              variant="outline"
            >
              Zamknij
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Helper functions
function getLeaveTypeColor(typeInfo: any, opacity: number): string {
  // If typeInfo is a string, use it as code
  if (typeof typeInfo === 'string') {
    const colors: Record<string, string> = {
      'wypoczynkowy': `rgba(34, 197, 94, ${opacity})`,  // Green
      'chorobowy': `rgba(239, 68, 68, ${opacity})`,  // Red
      'na_zadanie': `rgba(59, 130, 246, ${opacity})`, // Blue
      'okolicznosciowy': `rgba(168, 85, 247, ${opacity})`,  // Purple
      'bezplatny': `rgba(251, 146, 60, ${opacity})`, // Orange
      'szkoleniowy': `rgba(250, 204, 21, ${opacity})`, // Yellow
      'AL': `rgba(34, 197, 94, ${opacity})`,  // Green (legacy)
      'SL': `rgba(239, 68, 68, ${opacity})`,  // Red (legacy)
    };
    return colors[typeInfo?.toLowerCase()] || `rgba(107, 114, 128, ${opacity})`; // Gray default
  }
  
  // If typeInfo is an object, use name or color
  const name = typeInfo?.name?.toLowerCase() || '';
  const color = typeInfo?.color;
  
  // If color is provided in the leaveType object, use it
  if (color) {
    // Convert hex to rgba if needed
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color;
  }
  
  // Otherwise use name-based colors
  const colors: Record<string, string> = {
    'wypoczynkowy': `rgba(34, 197, 94, ${opacity})`,  // Green
    'chorobowy': `rgba(239, 68, 68, ${opacity})`,  // Red
    'na_zadanie': `rgba(59, 130, 246, ${opacity})`, // Blue
    'okolicznosciowy': `rgba(168, 85, 247, ${opacity})`,  // Purple
    'bezplatny': `rgba(251, 146, 60, ${opacity})`, // Orange
    'szkoleniowy': `rgba(250, 204, 21, ${opacity})`, // Yellow
  };
  return colors[name] || `rgba(107, 114, 128, ${opacity})`; // Gray default
}

function getLeaveTypeName(code: string): string {
  const names: Record<string, string> = {
    'wypoczynkowy': 'Wypoczynkowy',
    'chorobowy': 'Chorobowy',
    'na_zadanie': 'Na żądanie',
    'okolicznosciowy': 'Okolicznościowy',
    'bezplatny': 'Bezpłatny',
    'szkoleniowy': 'Szkoleniowy',
    'AL': 'Annual Leave',
    'SL': 'Sick Leave',
    'WFH': 'Work from Home',
    'OT': 'Overtime Leave',
  };
  return names[code?.toLowerCase()] || code;
}

function getMostCommonLeaveType(leaves: TeamLeave[] | undefined): string {
  if (!leaves || leaves.length === 0) return 'Brak';
  
  const typeCounts = leaves.reduce((acc: Record<string, number>, leave) => {
    const type = leave.leaveType?.name || (typeof leave.leaveType === 'string' ? leave.leaveType : 'Unknown');
    if (type) {
      acc[type] = (acc[type] || 0) + 1;
    }
    return acc;
  }, {});
  
  const [mostCommon] = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);
  return mostCommon ? mostCommon[0] : 'Brak';
}